import type { BlotterOrderEntry } from '@/store/blotter/types/BlotterOrderEntry.ts';
import { getOMSCurrencyFromBlotterCurrency } from '@/store/orders/helpers.ts';
import type { ClientWay, OrderStateCurrencyPair } from '@/store/orders/types.ts';
import type { Order, OrderMode } from '@/store/types.ts';
import type { OmsErrors, OmsWarning } from '@/types/common.ts';
import type { Currency } from '@/types/currency.ts';
import type { ISODate, StringTime } from '@/types/date.ts';

export type StopLossOrder = Order<'StopLossOrder', StopLossOrderProperties>;

export type StopLossOrderProperties = {
  status:
    | { type: 'creating' }
    | { type: 'pending' }
    | { type: 'failed'; errorMessage: string }
    | { type: 'submitted' | 'editing' | 'cancelled'; tradeId: string };
  clientWay: ClientWay;
  notional?: number;
  notionalCurrencyIndex: 1 | 2;

  currencyPair?: OrderStateCurrencyPair;
  limitPrice?: number;
  expiry: GoodTillCancelled | ExpiryDate;
  trigger: Trigger;

  notificationEmails?: string[];

  isReadyToSubmit: boolean;
  clockOffset?: number; // offset in milliseconds between the backend and the client

  // to detect if the order is in EDIT or VIEW mode
  // EDIT mode is used when the user is editing an existing order
  // VIEW mode is used when the user is viewing an existing order
  mode: OrderMode;

  errors?: OmsErrors;
  warnings?: OmsWarning;
};

export type StopLossOrderError = {
  field?: StopLossOrderFieldName;
  message: string;
  code: number;
};

export enum StopLossOrderFieldName {
  id = 'id',
  currencyPair = 'currencyPair',
  bdrId = 'bdrId',
  way = 'way',
  notional = 'notional',
  currency = 'currency',
  limitPrice = 'limitPrice',
  expiryDate = 'expiryDate',
  trigger = 'trigger',
  notificationEmails = 'notificationEmails',
}

export const triggers = ['Spot', 'BidOffer', 'RateOneTouch'] as const;
export type Trigger = (typeof triggers)[number];

export type GoodTillCancelled = {
  type: 'GoodTillCancelled';
};

export type ExpiryDate = {
  type: 'ExpiryDate';
  date?: ISODate;
  time?: StringTime;
};

export const generateStopLossOrder = (): StopLossOrder => {
  return {
    type: 'StopLossOrder',
    properties: {
      status: { type: 'creating' },
      clientWay: 'buy',
      notional: undefined,
      notionalCurrencyIndex: 1,
      currencyPair: undefined,
      limitPrice: undefined,
      expiry: { type: 'GoodTillCancelled' },
      trigger: 'Spot',
      notificationEmails: undefined,
      errors: {},
      isReadyToSubmit: false,
      clockOffset: 0,
      mode: 'VIEW',
    },
  };
};

export const generateStopLossOrderFromBlotter = (entry: BlotterOrderEntry, mode: OrderMode): StopLossOrder => {
  const currencyPair = getOMSCurrencyFromBlotterCurrency(entry.currencyPair);

  return {
    id: entry.id,
    type: 'StopLossOrder',
    properties: {
      mode,
      currencyPair,
      status: { type: 'submitted', tradeId: entry.id },
      limitPrice: entry.limitPrice,
      expiry: entry.isGtc
        ? { type: 'GoodTillCancelled' }
        : { type: 'ExpiryDate', date: entry.expiryDay ?? undefined, time: entry.expiryTime ?? undefined },
      notificationEmails: undefined,
      isReadyToSubmit: false,
      clientWay: entry.yourWay === 'Buy' ? 'buy' : 'sell',

      notional: entry.notional ?? undefined,
      notionalCurrencyIndex: entry.notionalCurrency === currencyPair?.currency1 ? 1 : 2,

      trigger: 'Spot', // not displayed in order summary and not present in blotter data, maybe should in blotter entry AND displayed
      clockOffset: 0,
    },
  };
};

export type ValidateStopLossOrderRequest = {
  tileId: string;
  clientId: number;
  way?: ClientWay;
  notional?: number;
  notionalCurrencyIndex: 1 | 2;
  currency1: Currency;
  currency2: Currency;

  limitPrice?: number;
  expiry: GoodTillCancelled | ExpiryDate;
  trigger: Trigger;
  notificationEmails?: {
    isOverridenEmails?: boolean;
    emails: string[];
  };
  clockOffset?: number; // offset in milliseconds between the backend and the client
};

export type SubmitStopLossOrderRequest = {
  tileId: string;
  clientId: number;
  way: ClientWay;
  notional: number;
  notionalCurrencyIndex: 1 | 2;
  currency1: Currency;
  currency2: Currency;
  limitPrice: number;
  expiry: GoodTillCancelled | ExpiryDate;
  trigger: Trigger;
  notificationEmails?: string[];
  clockOffset?: number; // offset in milliseconds between the backend and the client
};

export const mapStopLoss = (
  product: {
    type: 'StopLossOrder';
    clientId?: number;
    properties: StopLossOrderProperties;
    tileId: string;
  },
  defaultClientId: number,
): ValidateStopLossOrderRequest => {
  return {
    tileId: product.tileId,
    currency1: product.properties.currencyPair?.currency1 ?? '',
    currency2: product.properties.currencyPair?.currency2 ?? '',
    clientId: product.clientId ?? defaultClientId,
    way: product.properties.clientWay,
    notional: product.properties.notional,
    trigger: product.properties.trigger,
    notionalCurrencyIndex: product.properties.notionalCurrencyIndex,
    limitPrice: product.properties.limitPrice,
    expiry: product.properties.expiry,
    notificationEmails: {
      isOverridenEmails: false,
      emails: product.properties.notificationEmails ?? [],
    },
    clockOffset: 0,
  };
};
